import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import AuthCallback from "@/components/AuthCallback";
import AppShell from "@/components/layout/AppShell";
import LoginPage from "@/pages/LoginPage";
import RegisterPage from "@/pages/RegisterPage";
import PendingPage from "@/pages/PendingPage";
import DashboardPage from "@/pages/DashboardPage";
import POSPage from "@/pages/POSPage";
import InventoryPage from "@/pages/InventoryPage";
import SalesPage from "@/pages/SalesPage";
import AttendancePage from "@/pages/AttendancePage";
import StaffPage from "@/pages/StaffPage";
import CustomersPage from "@/pages/CustomersPage";
import BIRCenterPage from "@/pages/BIRCenterPage";
import SubscriptionPage from "@/pages/SubscriptionPage";
import AuditPage from "@/pages/AuditPage";
import AdminPage from "@/pages/AdminPage";
import PayrollPage from "@/pages/PayrollPage";
import OrdersPage from "@/pages/OrdersPage";
import PortalPage from "@/pages/PortalPage";
import SettingsPage from "@/pages/SettingsPage";
import ForgotPasswordPage from "@/pages/ForgotPasswordPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import { Loader2, ShieldAlert } from "lucide-react";

export function FullLoader() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F8FAFC]" data-testid="app-loading">
      <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
    </div>
  );
}

const ROUTE_MODULES = {
  "/dashboard": "dashboard", "/pos": "pos", "/inventory": "inventory", "/sales": "sales",
  "/orders": "orders", "/attendance": "attendance", "/customers": "clients", "/staff": "staff",
  "/payroll": "payroll", "/bir": "bir", "/subscription": "subscription", "/audit": "audit",
};

function HomeRedirect() {
  const { user, canAccessModule } = useAuth();
  if (user === null) return <FullLoader />;
  if (user === false) return <Navigate to="/login" replace />;
  if (user.status === "pending" || user.role === "pending") return <Navigate to="/pending" replace />;
  if (user.role === "system_admin") return <Navigate to="/admin" replace />;
  const preferred = user.role === "cashier" ? "/pos" : "/dashboard";
  const preferredModule = ROUTE_MODULES[preferred];
  if (canAccessModule(preferredModule)) return <Navigate to={preferred} replace />;
  const firstAllowed = Object.entries(ROUTE_MODULES).find(([, m]) => canAccessModule(m));
  if (firstAllowed) return <Navigate to={firstAllowed[0]} replace />;
  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F8FAFC] p-6" data-testid="no-access-page">
      <p className="text-sm text-slate-500">Your role currently has no module access. Please contact the platform administrator.</p>
    </div>
  );
}

function NoAccess() {
  return (
    <div className="py-24 text-center space-y-3" data-testid="module-no-access">
      <ShieldAlert className="h-10 w-10 mx-auto text-slate-300" />
      <h2 className="font-heading text-xl font-bold text-slate-900">No access to this module</h2>
      <p className="text-sm text-slate-500">The platform administrator can grant access in Admin Console → Role Permissions.</p>
    </div>
  );
}

function Protected({ children, roles, module }) {
  const { user, canAccessModule } = useAuth();
  if (user === null) return <FullLoader />;
  if (user === false) return <Navigate to="/login" replace />;
  if (user.status === "pending" || user.role === "pending") return <Navigate to="/pending" replace />;
  if (roles && !roles.includes(user.role)) return <Navigate to="/" replace />;
  if (module && !canAccessModule(module)) {
    return <AppShell><NoAccess /></AppShell>;
  }
  return <AppShell>{children}</AppShell>;
}

function AppRouter() {
  const location = useLocation();
  // Check URL fragment (not query params) for session_id — synchronous, prevents race conditions
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route path="/pending" element={<PendingPage />} />
      <Route path="/portal/*" element={<PortalPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/" element={<PortalPage />} />
      <Route path="/home" element={<HomeRedirect />} />
      <Route path="/dashboard" element={<Protected roles={["owner", "manager", "system_admin"]} module="dashboard"><DashboardPage /></Protected>} />
      <Route path="/pos" element={<Protected roles={["owner", "manager", "cashier", "system_admin"]} module="pos"><POSPage /></Protected>} />
      <Route path="/inventory" element={<Protected roles={["owner", "manager", "system_admin"]} module="inventory"><InventoryPage /></Protected>} />
      <Route path="/sales" element={<Protected roles={["owner", "manager", "cashier", "system_admin"]} module="sales"><SalesPage /></Protected>} />
      <Route path="/attendance" element={<Protected roles={["owner", "manager", "cashier", "system_admin"]} module="attendance"><AttendancePage /></Protected>} />
      <Route path="/staff" element={<Protected roles={["owner", "manager", "system_admin"]} module="staff"><StaffPage /></Protected>} />
      <Route path="/customers" element={<Protected roles={["owner", "manager", "cashier", "system_admin"]} module="clients"><CustomersPage /></Protected>} />
      <Route path="/bir" element={<Protected roles={["owner", "manager", "system_admin"]} module="bir"><BIRCenterPage /></Protected>} />
      <Route path="/payroll" element={<Protected roles={["owner", "manager", "system_admin"]} module="payroll"><PayrollPage /></Protected>} />
      <Route path="/orders" element={<Protected roles={["owner", "manager", "cashier", "system_admin"]} module="orders"><OrdersPage /></Protected>} />
      <Route path="/subscription" element={<Protected roles={["owner", "manager", "system_admin"]} module="subscription"><SubscriptionPage /></Protected>} />
      <Route path="/audit" element={<Protected roles={["owner", "manager", "system_admin"]} module="audit"><AuditPage /></Protected>} />
      <Route path="/settings" element={<Protected roles={["owner", "manager", "system_admin"]}><SettingsPage /></Protected>} />
      <Route path="/admin" element={<Protected roles={["system_admin"]}><AdminPage /></Protected>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <AppRouter />
          <Toaster position="top-right" richColors />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
